import { createContent, getContent } from '@/api/content-api';
import { assertToolSucceeded } from '@/api/templates-api';
import { useAddEntry, useSetFieldValue, useUpdateEntry } from '@/hooks/useTemplateQueries';
import type {
  ConversationItem as HistoryConversationItem,
  EntryFormAction,
  FieldInputAction,
} from '@vivport/backend/types';
import type { EntryFormSubmit } from './components/EntryFormActionCard';
import { ensureNeoChat, resumeSpeak2Me } from '@/api/speak2me-api';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useNavigateWithContentFlush, useUser } from '@/hooks';
import { contentKeys } from '@/hooks/useContentQueries';
import { billingKeys } from '@/hooks/useBillingQueries';
import { MessageDraftSendDialog } from '@/components/message/MessageDraftSendDialog';
import { enqueueSerial } from '@/lib/serial-queue';
import { useAsyncStore } from '@/store/asyncStore';
import { useBillingUiStore } from '@/store/billingUiStore';
import { useMemberStore } from '@/store/memberStore';
import { useSelectedItemStore } from '@/store/selectedItemStore';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { useSpeak2MeKickoffStore } from '@/store/speak2meKickoffStore';
import { useComposerStore } from '@/store/useComposerStore';
import {
  meetsObservationNewThresholds,
  observationOptions,
  projectObservations,
  type ObservationProjection,
} from '@/components/provenance/observation-labels';
import { useEvidenceToolStore } from '@/store/useEvidenceToolStore';
import { useCaptureFlowStore } from '@/store/useCaptureFlowStore';
import { OBSERVATION_CAPTURE_LABELS } from '@/components/widget/observation-capture/labels';
import { useEventComposerStore } from '@/store/useEventComposerStore';
import { getAccessToken } from '@/utils/token-utils';
import { toContentHref } from '@/utils/content-ref';
import {
  ConversationItem,
  MoveObservationMediaResult,
  Speak2MeTurnRequest,
  SuggestedAction,
} from '@vivport/backend/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useCoalescedInvalidation } from '@/hooks/useCoalescedInvalidation';
import { useLocation } from 'react-router-dom';
import { buildSpeak2MeViewContext, useViewContextStore } from '@/store/viewContextStore';
import { useDocumentProposalStore } from '@/store/documentProposalStore';
import { useVoiceSession, type ComposerDictation } from './useVoiceSession';
import { type StagedAttachment } from './components/ChatComposer';
import {
  createInitialNeoConversationState,
  neoConversationReducer,
} from './neo-state';
import { readSpeak2MeStream } from './turn-stream';
import { hasSpokenText, splitSpeakableSentences } from './voice/spoken-text';
import {
  type EphemeralPhase,
  type EphemeralToolCall,
} from './EphemeralThinkingSurface';
import {
  CONVERSATION_LOAD_FAILED_MESSAGE,
  FILE_UPLOAD_FAILED_MESSAGE,
  buildErrorAssistantItem,
  buildGreetingAssistantItem,
  TURN_REQUEST_FAILED_MESSAGE,
  MemberFacingError,
} from './panel-errors';
import {
  isoToLocalInput,
  registerContentTaskFromConversationItem,
  ephemeralReducer,
  type EphemeralState,
} from './panel-state';
import { Speak2MePanelBody, type Speak2MePanelBodyProps } from './components/Speak2MePanelBody';
// One panel is mounted at a time, so one key serialises every turn however it was asked
// for — typed, retried, or raised by a capture submit.
const SPEAK2ME_TURN_KEY = 'speak2me-turn';
// Voice mode narrates at most this many tool rounds per turn; later rounds are voided
// so a long investigation does not narrate itself five times over.
const VOICE_INTERIM_MAX_ROUNDS = 2;

export function Speak2MePanel() {
  const queryClient = useQueryClient();
  const coalescedContentRefresh = useCoalescedInvalidation([['content-ref']]);
  const isCreating = useSpeak2MeStore((state) => state.isOpen);
  const setIsCreating = useSpeak2MeStore((state) => state.setIsOpen);
  const resumeNeoChatId = useSpeak2MeStore((state) => state.resumeNeoChatId);
  const setResumeNeoChatId = useSpeak2MeStore((state) => state.setResumeNeoChatId);
  const resumeProvenance = useSpeak2MeStore((state) => state.resumeProvenance);
  const setResumeProvenance = useSpeak2MeStore((state) => state.setResumeProvenance);
  const openAsFreshChat = useSpeak2MeStore((state) => state.openAsFreshChat);
  const setOpenAsFreshChat = useSpeak2MeStore((state) => state.setOpenAsFreshChat);
  const openInDefaultInputMode = useSpeak2MeStore((state) => state.openInDefaultInputMode);
  const setOpenInDefaultInputMode = useSpeak2MeStore((state) => state.setOpenInDefaultInputMode);
  const setView = useSpeak2MeStore((state) => state.setView);
  const selectedLlmModelId = useSpeak2MeStore((state) => state.selectedLlmModelId);
  const dictationDeviceId = useSpeak2MeStore((state) => state.dictationDeviceId);
  const setDictationDeviceId = useSpeak2MeStore((state) => state.setDictationDeviceId);
  const hasOpenedSpeak2MeDesktop = useMemberStore((state) => state.hasOpenedSpeak2MeDesktop);
  const setHasOpenedSpeak2MeDesktop = useMemberStore((state) => state.setHasOpenedSpeak2MeDesktop);
  const clearSelectedItems = useSelectedItemStore((state) => state.clearSelectedItems);
  const pendingKickoff = useSpeak2MeKickoffStore((state) => state.pendingKickoff);
  const clearPendingKickoff = useSpeak2MeKickoffStore((state) => state.clearPendingKickoff);
  const navigate = useNavigateWithContentFlush();
  const { data: memberContext } = useUser();
  const [sendDialogItem, setSendDialogItem] = useState<Extract<ConversationItem, { kind: 'draft_send_ready' }> | null>(null);
  const openedSendDialogIdsRef = useRef(new Set<string>());
  const openedComposeIdsRef = useRef(new Set<string>());
  const openCompose = useComposerStore((state) => state.openCompose);
  const openedEventComposeIdsRef = useRef(new Set<string>());
  const openedSectionProposalIdsRef = useRef(new Set<string>());
  const openEventCompose = useEventComposerStore((state) => state.openEventCompose);
  const location = useLocation();
  const resumeMutation = useMutation({
    mutationFn: (neoChatId?: number) => resumeSpeak2Me(neoChatId ? { neoChatId } : {}),
  });

  const [state, dispatch] = useReducer(
    neoConversationReducer,
    undefined,
    () => createInitialNeoConversationState()
  );
  const [ephemeral, dispatchEphemeral] = useReducer(ephemeralReducer, { phase: 'hidden' } as EphemeralState);
  const [composerText, setComposerText] = useState('');
  const [stagedAttachments, setStagedAttachments] = useState<StagedAttachment[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [sessionEpoch, setSessionEpoch] = useState(0);

  const threadBottomRef = useRef<HTMLDivElement | null>(null);
  const bootstrapIntentRef = useRef<string | null>(null);
  const latestNeoChatIdRef = useRef<number | undefined>(undefined);
  const neoChatInitTaskIdRef = useRef<string | null>(null);
  // Every live media item in the history is produced by the capture handler below, so an
  // id absent from this set can only have come from replayed history — which is the only
  // case the staleness sweep is for.
  const resumeEpochRef = useRef(0);
  const suppressImplicitHydrationRef = useRef(false);
  // Read by the capture handler, which is registered once and would otherwise close over
  // a stale projection and a stale member.
  const observationProjectionRef = useRef<ObservationProjection | null>(null);
  const memberContextRef = useRef(memberContext);
  memberContextRef.current = memberContext;
  const ephemeralControllerRef = useRef<AbortController | null>(null);
  // The whole turn, not just its text: an attachment-only turn has no text to retry with, and
  // retrying with the text alone silently drops the files Neo was asked to work from.
  const lastTurnRef = useRef<{
    text: string;
    attachmentIds?: Speak2MeTurnRequest['attachmentIds'];
  }>({ text: '' });
  // Speak-while-generating buffer for the CURRENT iteration only. In voice mode a tool
  // iteration IS narrated: its leading *header* line is consumed unspoken and the prose
  // beneath it streams into speak() until tool_call_start voids the remainder. The buffer
  // resets on every iteration_start and only the final iteration reconciles against the
  // appended assistant_message. spokenChars is an offset into raw (the splitter is
  // concatenation-preserving); interimQueued counts sentences speak() actually queued.
  const streamSpeechRef = useRef<{
    raw: string;
    spokenChars: number;
    voided: boolean;
    headerResolved: boolean;
    interimQueued: number;
    toolRoundCounted: boolean;
  } | null>(null);
  const voiceRoundRef = useRef(0);

  const isTurnInFlight = ephemeral.phase === 'streaming';

  const voiceFeatureEnabled = import.meta.env.VITE_SPEAK2ME_VOICE === 'true';
  const isHydrationBlocked = isCreating && state.hydration.status === 'loading';
  const dictationBaseTextRef = useRef('');
  const voiceSession = useVoiceSession({
    enabled: voiceFeatureEnabled,
    suspended: isTurnInFlight || isUploadingFiles || isHydrationBlocked,
    dictationDeviceId,
    activeNeoChatId:
      state.activeProvenanceType === 'NEO_CHAT' || state.activeProvenanceType === undefined
        ? state.activeProvenanceId
        : undefined,
    onNeoChatBound: (neoChatId) => {
      dispatch({ type: 'SET_ACTIVE_PROVENANCE', provenanceId: neoChatId, provenanceType: 'NEO_CHAT' });
    },
    onDictationText: (text) => {
      setComposerText([dictationBaseTextRef.current, text].filter(Boolean).join(' ').trim());
    },
    onUtterance: (text) => {
      void processUserTurn(text);
    },
    onTurnBargeIn: () => {
      // Interrupting the interim acknowledgement must not abort a tool round mid-flight; the
      // utterance accumulates and submits after turn_complete instead (voice loop 5/6).
      // Returning false tells the hook the turn is still live, so its later sentences —
      // including the final answer — must still be spoken.
      if (streamSpeechRef.current?.voided) return false;
      ephemeralControllerRef.current?.abort();
      return true;
    },
    onGreeting: (text) => {
      dispatch({ type: 'APPEND_HISTORY_ITEMS', items: [buildGreetingAssistantItem(text)] });
    },
  });
  const voiceEnabled = voiceFeatureEnabled && voiceSession.isSupported;
  const dictation: ComposerDictation = useMemo(
    () => ({
      supported: voiceFeatureEnabled && voiceSession.isDictationSupported,
      active: voiceSession.mode === 'dictation',
      starting: voiceSession.isDictationStarting && voiceSession.mode !== 'dictation',
      finalizing: voiceSession.status === 'finalizing',
      analyser: voiceSession.dictationAnalyser,
      selectedDeviceId: dictationDeviceId,
      listDevices: voiceSession.listAudioInputs,
      selectDevice: setDictationDeviceId,
      start: () => {
        dictationBaseTextRef.current = composerText;
        voiceSession.startDictation();
      },
      accept: () => {
        voiceSession.stopDictation(true);
      },
      cancel: () => {
        voiceSession.stopDictation(false);
        setComposerText(dictationBaseTextRef.current);
      },
    }),
    [voiceFeatureEnabled, voiceSession, dictationDeviceId, setDictationDeviceId, composerText]
  );
  const stopVoiceSession = voiceSession.stop;
  const speakReply = voiceSession.speak;
  const turnLatency = voiceSession.turnLatency;
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(min-width: 768px)').matches;
  });

  useEffect(() => {
    threadBottomRef.current?.scrollIntoView({ block: 'end' });
  }, [state.history.length]);

  useEffect(() => {
    latestNeoChatIdRef.current =
      state.activeProvenanceType === 'NEO_CHAT' || state.activeProvenanceType === undefined
        ? state.activeProvenanceId
        : undefined;
  }, [state.activeProvenanceId, state.activeProvenanceType]);

  useEffect(() => () => {
    ephemeralControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const onMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktopViewport(event.matches);
    };

    setIsDesktopViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', onMediaChange);
    };
  }, []);

  useEffect(() => {
    if (isCreating && isDesktopViewport && !hasOpenedSpeak2MeDesktop) {
      setHasOpenedSpeak2MeDesktop(true);
    }
  }, [isCreating, isDesktopViewport, hasOpenedSpeak2MeDesktop, setHasOpenedSpeak2MeDesktop]);

  useEffect(() => {
    if (!isCreating) {
      return;
    }
    useEvidenceToolStore.getState().activateTool('chat');
  }, [isCreating]);

  // Capture and the voice loop both want the microphone, and capture replaces the
  // composer the loop is speaking into — so opening capture closes the loop, however it
  // was opened (the evidence icon, Neo's tool, or a capture affordance in the feed).
  const activeEvidenceToolId = useEvidenceToolStore((state) => state.activeToolId);
  useEffect(() => {
    if (activeEvidenceToolId !== 'observation') return;
    if (voiceSession.mode === 'off') return;
    stopVoiceSession();
  }, [activeEvidenceToolId, voiceSession.mode, stopVoiceSession]);

  useEffect(() => {
    if (!isCreating) {
      return;
    }
    if (openAsFreshChat) {
      stopVoiceSession();
      setComposerText('');
      setResumeNeoChatId(null);
      setResumeProvenance(null);
      resumeEpochRef.current += 1;
      setSessionEpoch((n) => n + 1);
      dispatch({ type: 'RESET_SESSION' });
      suppressImplicitHydrationRef.current = true;
      setOpenAsFreshChat(false);
      return;
    }
    if (suppressImplicitHydrationRef.current) {
      suppressImplicitHydrationRef.current = false;
      return;
    }
    // Only run implicit hydration when opening from idle state.
    // Explicit resume requests (resumeNeoChatId) can run anytime while open.
    if (resumeNeoChatId == null && state.hydration.status !== 'idle') {
      return;
    }
    let isCancelled = false;
    const epoch = resumeEpochRef.current;
    dispatch({ type: 'HYDRATION_STARTED' });
    void resumeMutation
      .mutateAsync(resumeNeoChatId ?? latestNeoChatIdRef.current)
      .then((response) => {
        if (isCancelled || resumeEpochRef.current !== epoch) {
          return;
        }
        dispatch({ type: 'HYDRATE_FROM_RESUME', response });
        setResumeNeoChatId(null);
      })
      .catch((error: unknown) => {
        if (isCancelled || resumeEpochRef.current !== epoch) {
          return;
        }
        console.error('Speak2Me could not load the previous conversation', error);
        dispatch({
          type: 'HYDRATION_FAILED',
          message: CONVERSATION_LOAD_FAILED_MESSAGE,
        });
        setResumeNeoChatId(null);
      });
    return () => {
      isCancelled = true;
    };
  }, [isCreating, resumeNeoChatId, setResumeNeoChatId, openAsFreshChat, setOpenAsFreshChat, stopVoiceSession]);

  // The Speak2Me button is the only caller that asks for this; openAsFreshChat alone means
  // "reset the conversation", which the Neo intro tour also does without wanting voice.
  useEffect(() => {
    if (!isCreating || !openInDefaultInputMode) return;
    // Consume through the live store, not this render's closure: StrictMode invokes the
    // effect twice against the same closure, and the second pass must find it spent.
    if (!useSpeak2MeStore.getState().openInDefaultInputMode) return;
    setOpenInDefaultInputMode(false);
    const mode = memberContext?.selectedMember.neoDefaultInputMode ?? 'VOICE';
    if (mode === 'VOICE') {
      if (voiceEnabled) voiceSession.startConversation();
      return;
    }
    if (mode === 'DICTATION' && dictation.supported) dictation.start();
  }, [
    isCreating,
    openInDefaultInputMode,
    setOpenInDefaultInputMode,
    memberContext,
    voiceEnabled,
    voiceSession,
    dictation,
  ]);

  useEffect(() => {
    if (!isCreating || !pendingKickoff || isTurnInFlight || isHydrationBlocked) return;
    clearPendingKickoff();
    void processUserTurn(pendingKickoff);
  }, [isCreating, pendingKickoff, isTurnInFlight, isHydrationBlocked, clearPendingKickoff]);

  useEffect(() => {
    if (!isCreating || !resumeProvenance) return;
    if (resumeProvenance.type === 'NEO_CHAT') {
      setResumeNeoChatId(resumeProvenance.id);
      setResumeProvenance(null);
      return;
    }
    resumeEpochRef.current += 1;
    setSessionEpoch((n) => n + 1);
    dispatch({
      type: 'SET_ACTIVE_PROVENANCE',
      provenanceId: resumeProvenance.id,
      provenanceType: resumeProvenance.type,
      provenanceCode: resumeProvenance.code,
    });
    setResumeProvenance(null);
  }, [isCreating, resumeProvenance, setResumeNeoChatId, setResumeProvenance]);

  const resetConversation = () => {
    ephemeralControllerRef.current?.abort();
    voiceSession.stop();
    setComposerText('');
    setStagedAttachments([]);
    setResumeNeoChatId(null);
    setResumeProvenance(null);
    resumeEpochRef.current += 1;
    setSessionEpoch((n) => n + 1);
    dispatch({ type: 'RESET_SESSION' });
    dispatchEphemeral({ type: 'HIDE' });
    useCaptureFlowStore.getState().reset();
    useEvidenceToolStore.getState().reset();
    setView('chat');
  };

  const invalidateNeoAndWorkflowCaches = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: contentKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['tag'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['tag-group'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['filters'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['processes'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['process-runs'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['process-run-entries'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['process-agent-options'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['process-skill-options'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['work4me-agent'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['work4me-skill'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['mission-control-runs'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['mission-control-approvals'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['mission-control-run'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['content-attachments'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['content-ref'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['calendar'], exact: false }),
      queryClient.invalidateQueries({ queryKey: billingKeys.spends() }),
      queryClient.invalidateQueries({ queryKey: billingKeys.summary() }),
    ]);
  }, [queryClient]);

  const runUserTurn = useCallback(async (
    userText: string,
    attachmentIds?: Speak2MeTurnRequest['attachmentIds'],
    appendMemberMessage: boolean = true,
  ): Promise<void> => {
    if (isHydrationBlocked) {
      dispatch({
        type: 'APPEND_HISTORY_ITEMS',
        items: [buildErrorAssistantItem('Speak2Me is still loading your previous conversation. Please retry in a moment.')],
      });
      return;
    }

    const value = userText.trim();
    const attachmentCount =
      (attachmentIds?.photoIds?.length ?? 0) +
      (attachmentIds?.recordingIds?.length ?? 0) +
      (attachmentIds?.videoIds?.length ?? 0) +
      (attachmentIds?.fileIds?.length ?? 0);
    // Handing over an attachment is a turn on its own — Neo asks for a file, the member
    // attaches it and sends. Only a turn carrying neither is nothing to send.
    if (!value && attachmentCount === 0) {
      return;
    }

    await useViewContextStore.getState().flushAll();

    const shouldTrackNeoChatInitialization = !state.activeProvenanceId;
    if (shouldTrackNeoChatInitialization && !neoChatInitTaskIdRef.current) {
      const asyncState = useAsyncStore.getState();
      neoChatInitTaskIdRef.current = asyncState.addTask({
        title: 'Generating Neo chat title and summary',
        description: 'Creating chat identity, title, and summary.',
        status: 'processing',
        contentType: 'NEO_CHAT',
      });
    }

    // An attachment-only turn appends no bubble: the attachment's own card states
    // what arrived, and persistence writes no member_message row for an empty
    // utterance either — live and reloaded threads show the same thing.
    if (appendMemberMessage && value) {
      dispatch({
        type: 'APPEND_HISTORY_ITEMS',
        items: [
          {
            id: `member-${nanoid(8)}`,
            kind: 'member_message',
            text: value,
          },
        ],
      });
    }

    const neoChatIdForTurn =
      state.activeProvenanceType === 'NEO_CHAT' || state.activeProvenanceType === undefined
        ? state.activeProvenanceId
        : undefined;
    const voiceConversation = voiceSession.mode === 'conversation';
    const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // A voice session minted before the member said anything has no chat yet; sending its id
    // lets the turn that creates the chat bind the session's cost to it.
    const voiceSessionId = voiceSession.getVoiceSessionId();
    const request: Speak2MeTurnRequest = {
      message: value,
      ...(neoChatIdForTurn !== undefined ? { neoChatId: neoChatIdForTurn } : {}),
      modelOverride: selectedLlmModelId === 'auto' ? null : selectedLlmModelId,
      ...(attachmentIds ? { attachmentIds } : {}),
      ...(voiceConversation ? { voiceConversation: true } : {}),
      ...(voiceSessionId ? { voiceSessionId } : {}),
      ...(clientTimezone ? { clientTimezone } : {}),
      viewContext: buildSpeak2MeViewContext(window.location.pathname),
    };

    lastTurnRef.current = { text: value, ...(attachmentIds ? { attachmentIds } : {}) };

    const controller = new AbortController();
    ephemeralControllerRef.current = controller;
    dispatchEphemeral({ type: 'START' });

    try {
      const token = getAccessToken();
      const memberId = localStorage.getItem('memberId');
      if (voiceConversation) turnLatency.beginTurn();
      voiceRoundRef.current = 0;
      const response = await fetch('/api/protected/speak2me/turn-stream', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(memberId ? { 'member-id': memberId } : {}),
        },
        body: JSON.stringify(request),
      });

      if (response.status === 401 && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
        return;
      }
      if (response.status === 402) {
        const body = await response.json().catch(() => null);
        const reason = body?.details?.reason;
        const paymentReason = reason === 'INSUFFICIENT_BALANCE' ? 'INSUFFICIENT_BALANCE' : 'NO_SUBSCRIPTION';
        useBillingUiStore.getState().setPaymentBlock({ reason: paymentReason });
        dispatch({
          type: 'APPEND_HISTORY_ITEMS',
          items: [
            buildErrorAssistantItem(
              paymentReason === 'INSUFFICIENT_BALANCE'
                ? "I can't respond right now — your account is out of AI credit. Add credit in Settings → Billing and I'll pick up right where we left off."
                : "I can't respond right now — your account needs an active subscription. You can set one up in Settings → Billing."
            ),
          ],
        });
        dispatchEphemeral({ type: 'HIDE' });
        return;
      }
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        console.error('Speak2Me turn request failed', { status: response.status, detail });
        throw new MemberFacingError(TURN_REQUEST_FAILED_MESSAGE);
      }

      for await (const event of readSpeak2MeStream(response, controller.signal)) {
        switch (event.type) {
          case 'neo_chat_ready':
            if (voiceConversation) turnLatency.setNeoChatId(event.neoChatId);
            dispatch({
              type: 'SET_ACTIVE_PROVENANCE',
              provenanceId: event.neoChatId,
              provenanceType: 'NEO_CHAT',
            });
            break;
          case 'text_delta': {
            if (voiceConversation) turnLatency.markFirstDelta();
            dispatchEphemeral({ type: 'TEXT_DELTA', text: event.text });
            const buf = streamSpeechRef.current;
            if (voiceConversation && buf && !buf.voided) {
              buf.raw += event.text;
              if (!buf.headerResolved) {
                if (/^\s*\*/.test(buf.raw)) {
                  // A leading *bold line* is the reasoning-preamble convention for
                  // tool iterations; hold speech until the first newline resolves it.
                  const nl = buf.raw.indexOf('\n');
                  if (nl === -1) break;
                  if (/^\s*\*[^*]+\*\s*$/.test(buf.raw.slice(0, nl))) {
                    if (voiceRoundRef.current > VOICE_INTERIM_MAX_ROUNDS) {
                      buf.voided = true;
                      break;
                    }
                    // The header is visual-only: consume it unspoken so the prose beneath it streams.
                    buf.spokenChars = nl + 1;
                  }
                }
                buf.headerResolved = true;
              }
              const pieces = splitSpeakableSentences(buf.raw.slice(buf.spokenChars));
              for (let i = 0; i < pieces.length - 1; i += 1) {
                const piece = pieces[i];
                buf.interimQueued += speakReply(piece);
                buf.spokenChars += piece.length;
              }
            }
            break;
          }
          case 'tool_call_start': {
            const buf = streamSpeechRef.current;
            // One tool_call_start fires per tool_use block, so a parallel-tool iteration
            // fires this case several times. A ROUND is the iteration, not the tool call:
            // only the first call of the iteration counts, or a two-tool round would report
            // toolRounds 2 and add the same interimQueued twice.
            if (voiceConversation && buf && !buf.toolRoundCounted) {
              buf.toolRoundCounted = true;
              turnLatency.markToolRound();
              turnLatency.markInterimSentences(buf.interimQueued);
            }
            if (buf) buf.voided = true;
            dispatchEphemeral({ type: 'TOOL_START', toolCallId: event.toolCallId, name: event.name });
            break;
          }
          case 'tool_call_input_delta':
            break;
          case 'tool_call_complete':
            dispatchEphemeral({ type: 'TOOL_AWAITING', toolCallId: event.toolCallId });
            break;
          case 'tool_executing':
            dispatchEphemeral({ type: 'TOOL_RUNNING', toolCallId: event.toolCallId });
            break;
          case 'tool_result': {
            const status: 'completed' | 'failed' | 'pending_approval' =
              event.status === 'success'
                ? 'completed'
                : event.status === 'pending_approval'
                  ? 'pending_approval'
                  : 'failed';
            dispatchEphemeral({ type: 'TOOL_DONE', toolCallId: event.toolCallId, status });
            break;
          }
          case 'conversation_item_appended': {
            dispatch({ type: 'APPEND_HISTORY_ITEMS', items: [event.item] });
            registerContentTaskFromConversationItem(event.item);
            const item = event.item;
            if (item.kind === 'capture_request') {
              useEvidenceToolStore.getState().openCapture({
                sessionId: item.id,
                defaultMode: item.defaultMode,
                linkContentId: item.linkContentId,
                linkTitle: item.linkTitle,
              });
            }
            if (item.kind === 'assistant_message') {
              const buf = voiceConversation ? streamSpeechRef.current : null;
              streamSpeechRef.current = null;
              if (!buf || buf.spokenChars === 0) {
                speakReply(item.text);
              } else if (item.text.startsWith(buf.raw.slice(0, buf.spokenChars))) {
                const remainder = item.text.slice(buf.spokenChars);
                if (remainder.trim()) speakReply(remainder);
              } else {
                console.error(
                  'Speak2Me voice: streamed sentences diverge from the appended assistant message; suppressing speech for this item to avoid a double-speak',
                  { spokenPrefix: buf.raw.slice(0, buf.spokenChars), itemText: item.text }
                );
              }
            }
            if (hasSpokenText(item)) {
              speakReply(item.spokenText);
            }
            if (item.kind === 'view_navigation') {
              navigate(item.url);
            }
            if (item.kind === 'page_navigation') {
              navigate(item.url);
            }
            if (item.kind === 'draft_send_ready' && !openedSendDialogIdsRef.current.has(item.id)) {
              openedSendDialogIdsRef.current.add(item.id);
              setSendDialogItem(item);
            }
            if (item.kind === 'compose_draft_ready' && !openedComposeIdsRef.current.has(item.id)) {
              openedComposeIdsRef.current.add(item.id);
              const composer = useComposerStore.getState();
              const mustFlush =
                composer.isOpen &&
                composer.draftContentId !== null &&
                composer.draftContentId !== item.draftContentId;
              const prefill = {
                draftContentId: item.draftContentId,
                to: item.to.join(', '),
                cc: item.cc.join(', '),
                bcc: item.bcc.join(', '),
                subject: item.subject,
                authoredBody: item.body,
              };
              void (async () => {
                if (mustFlush) {
                  await useViewContextStore.getState().flushAll();
                }
                openCompose(prefill);
              })();
            }
            if (item.kind === 'event_compose_ready' && !openedEventComposeIdsRef.current.has(item.id)) {
              openedEventComposeIdsRef.current.add(item.id);
              openEventCompose({
                title: item.title,
                startAt: isoToLocalInput(item.startAt, item.isAllDay),
                endAt: isoToLocalInput(item.endAt, item.isAllDay),
                isAllDay: item.isAllDay,
                location: item.location ?? '',
                description: item.description ?? '',
                attendees: item.attendees,
                sendInvite: false,
                accountId: item.suggestedAccountId ?? null,
                providerCalendarId: item.suggestedProviderCalendarId,
              });
            }
            if (
              item.kind === 'document_section_proposal_ready' &&
              !openedSectionProposalIdsRef.current.has(item.id)
            ) {
              openedSectionProposalIdsRef.current.add(item.id);
              const proposals = useDocumentProposalStore.getState();
              if (proposals.mountedDocumentCode !== item.documentCode) {
                navigate(toContentHref(item.documentCode));
              }
              proposals.deliver({
                id: item.id,
                documentCode: item.documentCode,
                sectionCode: item.sectionCode,
                text: item.text,
                ...(item.title !== undefined ? { title: item.title } : {}),
              });
            }
            if (
              item.kind === 'event_content_created' ||
              item.kind === 'event_document_section_created' ||
              item.kind === 'event_attachment_completed' ||
              item.kind === 'event_work4me_entity_created' ||
              item.kind === 'event_import_result'
            ) {
              void invalidateNeoAndWorkflowCaches();
            }
            break;
          }
          case 'iteration_start':
            if (voiceConversation) {
              voiceRoundRef.current += 1;
              streamSpeechRef.current = {
                raw: '',
                spokenChars: 0,
                voided: false,
                headerResolved: false,
                interimQueued: 0,
                toolRoundCounted: false,
              };
            }
            dispatchEphemeral({ type: 'NEW_ITERATION' });
            break;
          case 'iteration_complete':
            break;
          case 'turn_complete':
            dispatchEphemeral({ type: 'HIDE' });
            if (neoChatInitTaskIdRef.current) {
              const asyncState = useAsyncStore.getState();
              asyncState.updateTask(neoChatInitTaskIdRef.current, {
                status: 'completed',
                progress: 100,
                description: 'Neo chat title and summary generated.',
                contentId: state.activeProvenanceId,
              });
              neoChatInitTaskIdRef.current = null;
            }
            break;
          case 'neo_chat_metadata_updated':
            queryClient.setQueryData(contentKeys.detail(event.neoChatId), (prev: unknown) => {
              if (prev && typeof prev === 'object') {
                return { ...(prev as Record<string, unknown>), title: event.title, description: event.description };
              }
              return prev;
            });
            coalescedContentRefresh.invalidate([contentKeys.detail(event.neoChatId)]);
            break;
          case 'content_refresh':
            // documentRender is the per-id query family Document pages render from; the
            // detail-key invalidation cannot reach it (root-keyed). Family-level, mounted-only.
            coalescedContentRefresh.invalidate([
              ...event.contentIds.map((refreshContentId) => contentKeys.detail(refreshContentId)),
              [...contentKeys.all, 'documentRender'],
            ]);
            break;
          case 'turn_persisted':
            await coalescedContentRefresh.flush();
            await invalidateNeoAndWorkflowCaches();
            for (const createdId of event.createdContentIds) {
              if (Number.isInteger(createdId) && createdId > 0) {
                void queryClient.invalidateQueries({ queryKey: contentKeys.detail(createdId) });
              }
            }
            break;
          case 'error':
            // The stream's message is a backend diagnostic, not member-facing copy, so it
            // is logged rather than thrown as one.
            console.error('Speak2Me stream reported an error', event.message);
            throw new Error(event.message);
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        dispatchEphemeral({ type: 'CANCEL' });
        return;
      }
      turnLatency.abandon();
      if (neoChatInitTaskIdRef.current) {
        const asyncState = useAsyncStore.getState();
        asyncState.updateTask(neoChatInitTaskIdRef.current, {
          status: 'failed',
          description: 'Neo chat initialization failed.',
        });
        neoChatInitTaskIdRef.current = null;
      }
      const errorMessage =
        err instanceof MemberFacingError ? err.message : TURN_REQUEST_FAILED_MESSAGE;
      dispatch({
        type: 'APPEND_HISTORY_ITEMS',
        items: [buildErrorAssistantItem(errorMessage)],
      });
      dispatchEphemeral({ type: 'HIDE' });
    } finally {
      streamSpeechRef.current = null;
      if (ephemeralControllerRef.current === controller) {
        ephemeralControllerRef.current = null;
      }
    }
  }, [
    isHydrationBlocked,
    state.activeProvenanceId,
    state.activeProvenanceType,
    selectedLlmModelId,
    invalidateNeoAndWorkflowCaches,
    coalescedContentRefresh,
    queryClient,
    speakReply,
    turnLatency,
    voiceSession.mode,
    navigate,
    openCompose,
    openEventCompose,
  ]);

  // Evidence capture stays live while Neo is streaming, so a second turn can now be asked
  // for mid-reply. A turn owns the ephemeral phase and the abort controller, so two at
  // once would interleave into one stream and lose the first one's Cancel handle: they
  // queue instead. Nothing the member SEES waits on this — the capture handler below
  // dispatches its bubble and Observation card before calling here.
  const processUserTurn = useCallback(
    (
      userText: string,
      attachmentIds?: Speak2MeTurnRequest['attachmentIds'],
      appendMemberMessage: boolean = true,
    ): Promise<void> =>
      new Promise<void>((resolve) => {
        enqueueSerial(SPEAK2ME_TURN_KEY, async () => {
          try {
            await runUserTurn(userText, attachmentIds, appendMemberMessage);
          } finally {
            resolve();
          }
        });
      }),
    [runUserTurn],
  );

  useEffect(() => {
    useCaptureFlowStore.getState().registerHandler(
      ({ itemId, photoIds, recordingIds, videoIds, observationGroupId }) => {
        // One submit is one member action and one Observation, so the history gets the
        // member's turn plus a single observation item — the per-item content cards it
        // used to emit were never persisted and so never survived a reload anyway.
        const parts: string[] = [];
        if (photoIds.length > 0) {
          parts.push(OBSERVATION_CAPTURE_LABELS.session.photoCount(photoIds.length));
        }
        if (recordingIds.length > 0) {
          parts.push(OBSERVATION_CAPTURE_LABELS.session.recordingCount(recordingIds.length));
        }
        if (videoIds.length > 0) {
          parts.push(OBSERVATION_CAPTURE_LABELS.session.videoCount(videoIds.length));
        }
        const summary = OBSERVATION_CAPTURE_LABELS.session.captureSummary(parts);
        const attachmentIds = {
          ...(photoIds.length > 0 ? { photoIds } : {}),
          ...(recordingIds.length > 0 ? { recordingIds } : {}),
          ...(videoIds.length > 0 ? { videoIds } : {}),
          ...(observationGroupId !== null ? { observation: { groupId: observationGroupId } } : {}),
        };
        // No member bubble: the summary is the tool's words, not the member's, and the
        // Observation card below is the record of what was added. It still goes to the
        // model as this turn's user message so Neo knows a capture landed.
        const items = [
          ...(observationGroupId !== null
            ? [
                {
                  id: `obs-${observationGroupId}-${nanoid(6)}`,
                  kind: 'observation_media' as const,
                  schemaVersion: 2 as const,
                  observationGroupId,
                  photoIds,
                  recordingIds,
                  videoIds,
                },
              ]
            : []),
        ];
        dispatch({ type: 'APPEND_HISTORY_ITEMS', items });
        void processUserTurn(summary, attachmentIds, false);

        // The capture tool has already made this session sticky to the group it just used.
        // The member's thresholds are the only thing that un-sticks it, and only ever in
        // response to a submit — never reactively, so re-selecting a full Observation stays
        // selected. Counts union the group's existing media with this submit's.
        const member = memberContextRef.current?.selectedMember;
        if (observationGroupId !== null && member) {
          const merged = observationProjectionRef.current?.mergedByGroupId.get(observationGroupId);
          const counts = {
            recordings: new Set([...(merged?.recordingIds ?? []), ...recordingIds]).size,
            photos: new Set([...(merged?.photoIds ?? []), ...photoIds]).size,
            videos: new Set([...(merged?.videoIds ?? []), ...videoIds]).size,
          };
          const reachedThreshold = meetsObservationNewThresholds(counts, {
            recordings: member.observationNewAfterRecordings,
            photos: member.observationNewAfterPhotos,
            videos: member.observationNewAfterVideos,
          });
          if (reachedThreshold) {
            useCaptureFlowStore.getState().setSessionObservationGroup(itemId, null);
          }
        }
      }
    );
    return () => useCaptureFlowStore.getState().registerHandler(null);
  }, [processUserTurn]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const createMode = searchParams.get('create');
    if (!createMode) {
      return;
    }

    const bootstrapPrompts: Record<string, string> = {
      'sectioned-report': 'Create a new sectioned document.',
      'event-import': 'I want to import events from a spreadsheet file.',
    };
    const starterPrompt = bootstrapPrompts[createMode];
    const bootstrapKey = `${location.pathname}?${location.search}`;
    if (!starterPrompt || bootstrapIntentRef.current === bootstrapKey) {
      return;
    }

    bootstrapIntentRef.current = bootstrapKey;
    setIsCreating(true);

    const hydrationReady = state.hydration.status === 'hydrated' || state.hydration.status === 'error';
    if (hydrationReady && !isTurnInFlight && state.history.length === 0) {
      void processUserTurn(starterPrompt);
      setComposerText('');
    } else {
      setComposerText(starterPrompt);
    }

    navigate(location.pathname, { replace: true });
  }, [
    location.pathname,
    location.search,
    navigate,
    processUserTurn,
    setIsCreating,
    state.history.length,
    state.hydration.status,
    isTurnInFlight,
  ]);

  const submitComposerMessage = async () => {
    const value = composerText.trim();
    const attachments = stagedAttachments;
    if (!value && attachments.length === 0) return;
    const fileIds = attachments.map((attachment) => attachment.fileId);
    setComposerText('');
    setStagedAttachments([]);
    await processUserTurn(value, fileIds.length > 0 ? { fileIds } : undefined);
  };

  const removeStagedAttachment = (fileId: number) => {
    setStagedAttachments((current) => current.filter((attachment) => attachment.fileId !== fileId));
  };

  // The Add buttons on a photo/recording card open the same capture tool Neo opens through
  // neo.requestCapture — appended at the end of the thread and scrolled to, so the
  // member can use it straight away. Deliberately local: an unused tool is transient UI, and
  // a used one persists through the turn the capture handler fires on submit.
  const openCaptureTool = ({
    mode,
    linkContentId,
  }: {
    mode: 'recording' | 'photo' | 'video';
    linkContentId?: number;
  }) => {
    // A fresh conversation has no chat until the first turn POST creates one, so a capture
    // made before any turn had no apex to file against and the upload fell back to opening a
    // ManualSession. Binding the chat BEFORE the surface opens closes that race; the first
    // turn then joins the same chat via neoChatIdForTurn. On failure the surface still opens
    // and the upload takes the sanctioned fallback.
    if (state.activeProvenanceId === undefined) {
      void ensureNeoChat()
        .then((ensured) => {
          dispatch({
            type: 'SET_ACTIVE_PROVENANCE',
            provenanceId: ensured.neoChatId,
            provenanceType: 'NEO_CHAT',
          });
        })
        .catch(() => undefined);
    }
    const lastItem = state.history[state.history.length - 1];
    // One card offers both modes, so the mode asked for doesn't matter — but the link target
    // does. Asking for a linked capture while an unlinked card sits open must open a new card
    // rather than scroll the member to one that would file the capture nowhere.
    const isAlreadyOpen =
      lastItem?.kind === 'capture_request' &&
      (lastItem.linkContentId ?? null) === (linkContentId ?? null);
    if (isAlreadyOpen && lastItem.kind === 'capture_request') {
      useEvidenceToolStore.getState().openCapture({
        sessionId: lastItem.id,
        defaultMode: lastItem.defaultMode,
        linkContentId: lastItem.linkContentId,
        linkTitle: lastItem.linkTitle,
      });
      return;
    }
    const sessionId = `cap-req-${nanoid(8)}`;
    dispatch({
      type: 'APPEND_HISTORY_ITEMS',
      items: [
        {
          id: sessionId,
          kind: 'capture_request',
          schemaVersion: 2,
          defaultMode: mode,
              ...(linkContentId !== undefined ? { linkContentId } : {}),
        },
      ],
    });
    useEvidenceToolStore.getState().openCapture({
      sessionId,
      defaultMode: mode,
      ...(linkContentId !== undefined ? { linkContentId } : {}),
    });
  };

  const handleAttachFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) {
      return;
    }
    setIsUploadingFiles(true);
    try {
      const uploaded: StagedAttachment[] = [];
      for (const file of files) {
        const created = await createContent({
          contentType: 'FILE',
          file,
          title: file.name,
          uploadPurpose: 'CHAT_ATTACHMENT',
        });
        uploaded.push({ fileId: created.id, name: file.name });
      }
      if (uploaded.length === 0) {
        throw new Error('No files were uploaded.');
      }
      setStagedAttachments((current) => [...current, ...uploaded]);
    } catch (error) {
      console.error('Speak2Me file upload failed', error);
      dispatch({
        type: 'APPEND_HISTORY_ITEMS',
        items: [buildErrorAssistantItem(FILE_UPLOAD_FAILED_MESSAGE)],
      });
    } finally {
      setIsUploadingFiles(false);
    }
  };

  function extractStringPayload(
    payload: Record<string, unknown> | undefined,
    key: string
  ): string | undefined {
    const value = payload?.[key];
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
  }

  const setFieldValue = useSetFieldValue();
  // The card's value is written by the governed tool first; only then does it enter the
  // transcript as the member's own line, through the same call SEND_MESSAGE uses.
  const handleFieldInputSubmit = async (action: FieldInputAction, value: string): Promise<void> => {
    assertToolSucceeded(
      await setFieldValue.mutateAsync({
        instanceId: action.payload.instanceId,
        sectionCode: action.payload.sectionCode,
        fieldKey: action.payload.fieldKey,
        value,
      })
    );
    await processUserTurn(`${action.payload.label}: ${value}`);
  };
  const addEntry = useAddEntry();
  const updateEntry = useUpdateEntry();
  // Same shape as the field card: the governed tool writes first, then ONE member line echoes it.
  const handleEntryFormSubmit = async (action: EntryFormAction, submit: EntryFormSubmit): Promise<void> => {
    const { instanceId, sectionCode, def } = action.payload;
    if (submit.mode === 'add') {
      assertToolSucceeded(await addEntry.mutateAsync({ instanceId, sectionCode, ...submit.input }));
      await processUserTurn(`Added ${def.itemLabel}: ${submit.title}`);
      return;
    }
    if (submit.input !== null) {
      assertToolSucceeded(
        await updateEntry.mutateAsync({ instanceId, sectionCode, entryNumber: submit.entryNumber, ...submit.input })
      );
    }
    await processUserTurn(`Updated ${def.itemLabel}: ${submit.title}`);
  };
  const offeredActions = useMemo(() => latestOfferedActions(state.history), [state.history]);
  const conversationPhotoIds = useMemo(() => conversationPhotoIdsOf(state.history), [state.history]);

  const handleSuggestedAction = async (action: SuggestedAction): Promise<void> => {
    if (action.kind === 'FIELD_INPUT' || action.kind === 'ENTRY_FORM') return;
    if (action.kind === 'OPEN_SETTINGS') {
      const topic = extractStringPayload(action.payload, 'topic');
      const query = topic ? `?topic=${encodeURIComponent(topic)}` : '';
      navigate(`/settings${query}`);
      setIsCreating(false);
      resetConversation();
      return;
    }

    const suggestedPrompt =
      extractStringPayload(action.payload, 'message') ??
      extractStringPayload(action.payload, 'prompt') ??
      action.label;
    await processUserTurn(suggestedPrompt);
  };

  const closePanel = () => {
    ephemeralControllerRef.current?.abort();
    voiceSession.stop();
    setIsCreating(false);
    resetConversation();
  };

  const handleOpenContent = async (contentId: number) => {
    clearSelectedItems();
    const content = await getContent(contentId);
    navigate(toContentHref(content.code));
  };

  const ephemeralPhase: EphemeralPhase = ephemeral.phase;
  const ephemeralStreamingText = ephemeral.phase === 'streaming' ? ephemeral.streamingText : '';
  const ephemeralToolCalls = useMemo<EphemeralToolCall[]>(() => {
    if (ephemeral.phase === 'streaming' || ephemeral.phase === 'cancelled') {
      return ephemeral.toolCalls;
    }
    return [];
  }, [ephemeral]);

  // Memoised: the panel re-renders on every streamed token, and the selector and the
  // history cards must be built from the SAME projection or they could disagree.
  const observationProjection = useMemo(() => projectObservations(state.history), [state.history]);
  const observationOptionsForChat = useMemo(
    () => observationOptions(observationProjection),
    [observationProjection]
  );
  observationProjectionRef.current = observationProjection;

  // The chat pane's history is a reducer, not a query, so a move lands here as one more
  // appended item — the same shape the ledger persisted — and the projection folds it
  // over the capture rows on the next render.
  const handleObservationMediaMoved = useCallback(
    (result: MoveObservationMediaResult) => {
      dispatch({
        type: 'APPEND_HISTORY_ITEMS',
        items: [
          {
            id: result.conversationItemId ?? `obs-move-${result.contentId}-${nanoid(6)}`,
            kind: 'observation_media_moved' as const,
            schemaVersion: 1 as const,
            contentId: result.contentId,
            fromObservationGroupId: result.fromObservationGroupId,
            toObservationGroupId: result.toObservationGroupId,
          },
        ],
      });
    },
    []
  );

  const panelBodyProps: Speak2MePanelBodyProps = {
    closeControl: (
      <Button
        data-testid="obs-create-close-btn"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label="Close"
        onClick={closePanel}
      >
        <X className="h-4 w-4" />
      </Button>
    ),
    onResetConversation: resetConversation,
    history: state.history,
    bottomRef: threadBottomRef,
    onRequestCapture: openCaptureTool,
    observationOptions: observationOptionsForChat,
    // Same source the turn uses for neoChatId: a capture submitted here is filed against
    // this chat, so the upload route never falls back to opening a ManualSession.
    ...(state.activeProvenanceType === 'NEO_CHAT' && state.activeProvenanceId !== undefined
      ? { captureOriginProvenanceId: state.activeProvenanceId }
      : {}),
    onOpenContent: handleOpenContent,
    onObservationMediaMoved: handleObservationMediaMoved,
    suggestedActions: offeredActions,
    onActionClick: (action) => {
      void handleSuggestedAction(action);
    },
    onFieldInputSubmit: handleFieldInputSubmit,
    onEntryFormSubmit: handleEntryFormSubmit,
    conversationPhotoIds,
    disabled: isTurnInFlight || isUploadingFiles || isHydrationBlocked,
    // Capturing evidence is not conversing: a member out on site must be able to keep
    // photographing and recording while Neo is mid-reply, and a chat file upload has
    // nothing to do with the capture tool. Only an unhydrated chat blocks it, because
    // until then the session and its Observations are not yet known.
    captureDisabled: isHydrationBlocked,
    composerText,
    onComposerTextChange: setComposerText,
    canSend:
      !isHydrationBlocked && (composerText.trim().length > 0 || stagedAttachments.length > 0),
    onSend: () => {
      void submitComposerMessage();
    },
    onAttachFiles: (files) => {
      void handleAttachFiles(files);
    },
    stagedAttachments,
    onRemoveStagedAttachment: removeStagedAttachment,
    isUploadingFiles,
    voiceEnabled,
    voiceSession,
    dictation,
    activeProvenanceId: state.activeProvenanceId,
    activeProvenanceType: state.activeProvenanceType,
    activeProvenanceCode: state.activeProvenanceCode,
    chromeBusy: isTurnInFlight || isUploadingFiles,
    sessionEpoch,
    ephemeralPhase,
    ephemeralStreamingText,
    ephemeralToolCalls,
    onCancelTurn: () => {
      ephemeralControllerRef.current?.abort();
    },
    onRetryTurn: () => {
      void processUserTurn(lastTurnRef.current.text, lastTurnRef.current.attachmentIds, false);
    },
  };
  const showDesktopPane = isDesktopViewport && isCreating;

  return (
    <>
      {showDesktopPane ? (
        <aside
          data-testid="speak2me-panel"
          className="hidden h-full min-h-0 w-96 flex-shrink-0 flex-col overflow-hidden border-l bg-background md:flex"
        >
          <Speak2MePanelBody {...panelBodyProps} />
        </aside>
      ) : null}

      {!isDesktopViewport ? (
        <Sheet modal open={isCreating}>
          <SheetContent
            data-testid="speak2me-panel"
            className="flex h-full min-h-0 flex-col gap-0 overflow-hidden p-0 px-0"
            closeButton={false}
            showOverlay
            disableAnimations={hasOpenedSpeak2MeDesktop}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
            }}
            onPointerDownOutside={(event) => {
              event.preventDefault();
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Speak2Me panel</SheetTitle>
              <SheetDescription>Conversational assistant for guided content and workflow actions.</SheetDescription>
            </SheetHeader>
            <Speak2MePanelBody
              {...panelBodyProps}
              closeControl={
                <SheetClose asChild>
                  {panelBodyProps.closeControl}
                </SheetClose>
              }
            />
          </SheetContent>
        </Sheet>
      ) : null}

      <MessageDraftSendDialog
        open={sendDialogItem !== null}
        item={sendDialogItem}
        onClose={() => setSendDialogItem(null)}
      />
    </>
  );
}

// The offer Neo made this turn: the last field_input_offered or entry_form_offered item with no
// member message after it. Once the member answers (by tapping or typing) the row empties; Neo
// can offer again.
function latestOfferedActions(history: HistoryConversationItem[]): (FieldInputAction | EntryFormAction)[] {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const item = history[i];
    if (item.kind === 'member_message') return [];
    if (item.kind === 'field_input_offered') return item.actions;
    if (item.kind === 'entry_form_offered') return [item.action];
  }
  return [];
}

// Photos already in this conversation, so an entry form can reference them without a capture.
function conversationPhotoIdsOf(history: HistoryConversationItem[]): number[] {
  const ids = new Set<number>();
  for (const item of history) {
    if (item.kind === 'observation_media') item.photoIds.forEach((id) => ids.add(id));
    if (item.kind === 'event_content_created' && item.contentType === 'PHOTO') ids.add(item.contentId);
  }
  return [...ids];
}
